// The alarm surfaces' LIVE READING layer: the detail page's evaluation panel
// (custom + conditional alarms, phase 7, plan D8) and the list rows' value line
// ("Now 12.6 V · alarms below 11.8 V", ADR 0170). One pure module that answers,
// for a stored definition: what is each of its signals reading right now, does
// each clause hold, and what would the whole alarm do with those readings.
//
// It decides nothing new. The verdicts come from `customAlarmForm`
// (`wouldFireNow` / `clauseHolds` / `foldConditions`) and the combined sentence
// from `customAlarmConditions.liveVerdictLine` — the same functions the builder
// uses — reached through `initFromCustomDef`, which reads a STORED definition
// back into the builder's draft. That is the whole point: the page you author on
// and the page you read afterwards must never disagree about whether an alarm
// would be firing.
//
// WHERE THE VALUE COMES FROM, in order, and why it is labelled:
//
//   1. the live WS feed (`useBoatChrome().liveSamples`) — values as they arrive;
//   2. a stored snapshot — the signal catalog's `lastValue` and/or a one-shot
//      `GET /v1/boats/:boatId/telemetry/latest?paths=…`. Both are "the newest
//      sample the cloud has", so they are ranked against the live feed BY TIME,
//      not by which endpoint served them;
//   3. the runtime snapshot's own last value — the reading the alarm last
//      changed state on ("12.1 V at its last state change, 3 hr ago"). The boat
//      stamps that value at EVERY transition — firing, clearing, acknowledging —
//      so the row names the state change, never "when it last fired".
//
// Source 3 is deliberately NOT allowed to answer "right now". It is a value from
// a past transition, not a current sample, so it renders with its own wording
// and every verdict built on it is `null` ("we cannot say"). A boat that has
// gone offline therefore shows its last known reading with a timestamp and NO
// present-tense claim — the one degradation that must never look like a live
// answer.

import type { UnitSettings } from '@neomaris/protocol';

import {
  alarmConditionsSetup,
  isCustomDefinition,
  readWireTrigger,
  resolvedPathFor,
  setpointPhrase,
  triggerInVocabulary,
  unitVocabulary,
  type AlarmAlertRow,
  type AlarmWireTrigger,
  type UnitVocabulary,
} from './alarmDisplay';
import {
  convertedQuantity,
  distinctDecimals,
  formatDuration,
  formatThreshold,
  readConditions,
  type ConditionClauseSpec,
} from './alarmSentence';
import { categoryFor, type UnitCategory } from './displayUnits';
import {
  clauseChip,
  clauseExpectation,
  liveVerdictLine,
  type ClauseChip,
  type ClauseState,
  type LiveVerdictLine,
} from './customAlarmConditions';
import {
  clauseHolds,
  conditionDraftFromClause,
  draftSetpointStored,
  wouldFireNow,
  type ConditionDraft,
  type CustomAlarmDraft,
  type LiveReading,
} from './customAlarmForm';
import { formatRelativeShort } from './deviceSeen';
import { freshness, signalLabel, type Signal, type SignalValue } from './signalCatalog';
import { formatValue, readingScalar, type LiveSampleLike } from './signalLive';

/** Where a shown value came from — what lets the row word itself honestly. */
export type LiveValueSource = 'live' | 'stored' | 'lastTransition' | 'none';

/** One signal's current reading, resolved across every source. */
export interface LiveObservation {
  /** The scalar to print, already flattened (structured payloads are `null`). */
  readonly value: SignalValue;
  /** When it was observed, epoch-ms; `null` when nothing says. */
  readonly atMs: number | null;
  readonly source: LiveValueSource;
  /**
   * The path REPORTED, but with a payload that has no scalar to print — the
   * position object on `navigation.position` being the one every boat has.
   *
   * `value` is `null` for that and for "nothing arrived at all", and the two are
   * opposite answers: the stock `gps_loss` alarm watches a structured path, so
   * conflating them makes a boat with a perfectly good GPS fix read "No reading
   * · alarms if it stops reporting" — a false alarm in the only place that
   * matters, the words. {@link observationText} keeps them apart.
   */
  readonly structured: boolean;
}

/** One candidate reading offered to {@link resolveObservation}. */
export interface ObservationCandidate {
  readonly value: unknown;
  readonly atMs: number | null;
  readonly source: Exclude<LiveValueSource, 'none'>;
  /**
   * Force "reported, but unprintable" when the raw `value` cannot show it. The
   * signal catalog is exactly that case: a structured path arrives as
   * `kind: 'object'` with `lastValue: null` and a real `lastValueAt`, so only
   * the kind knows the value was ever there. A live WS frame carries the object
   * itself and needs no flag.
   */
  readonly structured?: boolean;
}

const NO_OBSERVATION: LiveObservation = {
  value: null,
  atMs: null,
  source: 'none',
  structured: false,
};

function candidateTime(candidate: ObservationCandidate): number {
  return typeof candidate.atMs === 'number' && Number.isFinite(candidate.atMs)
    ? candidate.atMs
    : Number.NEGATIVE_INFINITY;
}

/** Whether a raw value is PRESENT but has no printable scalar in it. */
function isStructuredValue(value: unknown): boolean {
  return typeof value === 'object' && value !== null;
}

/** Did this candidate report something, even if nothing printable came of it? */
function candidateStructured(candidate: ObservationCandidate): boolean {
  return candidate.structured === true || isStructuredValue(candidate.value);
}

/**
 * The reading to show, picked across the sources above.
 *
 * Current sources (`live` / `stored`) are ranked by TIME — a live frame and a
 * stored snapshot describe the same path, and the newer one is simply newer. A
 * timestamped candidate wins even when its value is unprintable (a structured
 * payload): the path IS reporting, and showing an older number as though it were
 * current is the one outcome worth avoiding. `lastTransition` is a last resort and
 * never competes on time: it is a value from a past transition, not a sample.
 */
export function resolveObservation(
  candidates: readonly (ObservationCandidate | null | undefined)[]
): LiveObservation {
  let best: ObservationCandidate | undefined;
  let lastTransition: ObservationCandidate | undefined;
  for (const candidate of candidates) {
    if (candidate === null || candidate === undefined) continue;
    if (candidate.source === 'lastTransition') {
      if (lastTransition === undefined) lastTransition = candidate;
      continue;
    }
    // Nothing to say: no timestamp AND nothing printable. An accepted path the
    // boat has never put a value on lands here, and must not preempt the
    // runtime's last-transition value below.
    const untimed = candidateTime(candidate) === Number.NEGATIVE_INFINITY;
    if (untimed && readingScalar(candidate.value) === null) continue;
    if (best === undefined || candidateTime(candidate) > candidateTime(best)) best = candidate;
  }
  if (best !== undefined) {
    const at = candidateTime(best);
    return {
      value: readingScalar(best.value),
      atMs: at === Number.NEGATIVE_INFINITY ? null : at,
      source: best.source,
      structured: candidateStructured(best),
    };
  }
  if (lastTransition !== undefined) {
    const value = readingScalar(lastTransition.value);
    const at = candidateTime(lastTransition);
    if (value !== null) {
      return {
        value,
        atMs: at === Number.NEGATIVE_INFINITY ? null : at,
        source: 'lastTransition',
        structured: false,
      };
    }
  }
  return NO_OBSERVATION;
}

/** The sources a page can offer, each optional and each possibly empty. */
export interface ObservationSources {
  /** The live WS feed for this boat (`useBoatChrome().liveSamples`). */
  readonly live?: ReadonlyMap<string, LiveSampleLike> | null;
  /** The signal catalog, by path — its `lastValue`/`lastValueAt` snapshot. */
  readonly catalog?: ReadonlyMap<string, Signal> | null;
  /** A one-shot `telemetry/latest` read, by path. */
  readonly latest?: ReadonlyMap<string, { readonly value: unknown; readonly time: string }> | null;
  /**
   * The runtime snapshot's own last value, and the ONE path it belongs to (the
   * alarm's pin) — a condition clause's path never gets it.
   */
  readonly lastTransition?: {
    readonly path: string;
    readonly value: unknown;
    readonly atIso?: string | null;
  } | null;
}

function isoMs(iso: string | null | undefined): number | null {
  if (iso === null || iso === undefined || iso === '') return null;
  const ms = Date.parse(iso);
  return Number.isFinite(ms) ? ms : null;
}

/** A per-path {@link resolveObservation} over the sources a page already holds. */
export function observationLookup(sources: ObservationSources): (path: string) => LiveObservation {
  return (path: string): LiveObservation => {
    if (path.length === 0) return NO_OBSERVATION;
    const live = sources.live?.get(path);
    const catalog = sources.catalog?.get(path);
    const latest = sources.latest?.get(path);
    const lastTransition = sources.lastTransition;
    return resolveObservation([
      live === undefined ? null : { value: live.value, atMs: live.receivedAt, source: 'live' },
      catalog === undefined
        ? null
        : {
            value: catalog.lastValue,
            atMs: isoMs(catalog.lastValueAt),
            source: 'stored',
            // A structured path's stored snapshot is `lastValue: null` with a
            // real `lastValueAt` — only `kind` says the value was ever there.
            structured: catalog.kind === 'object',
          },
      latest === undefined
        ? null
        : { value: latest.value, atMs: isoMs(latest.time), source: 'stored' },
      lastTransition === null || lastTransition === undefined || lastTransition.path !== path
        ? null
        : {
            value: lastTransition.value,
            atMs: isoMs(lastTransition.atIso),
            source: 'lastTransition',
          },
    ]);
  };
}

/**
 * The observation as a {@link LiveReading} the verdict functions can judge, or
 * `undefined` when it must not be judged at all. A `lastTransition` value is exactly
 * that case: it is real, it is worth showing, and it says nothing about now.
 */
export function observationReading(obs: LiveObservation): LiveReading | undefined {
  if (obs.source === 'none' || obs.source === 'lastTransition') return undefined;
  return { value: obs.value, ...(obs.atMs === null ? {} : { receivedAt: obs.atMs }) };
}

/**
 * One reading as text, in the account's units when the caller named a category.
 *
 * Numbers go through {@link formatThreshold}, which is what `formatValue` does
 * for them anyway — so with no category this is `formatValue`, character for
 * character. Everything else (On/Off, a mode string) has no unit to convert and
 * stays on `formatValue`. `minDecimals` is {@link distinctDecimals}' answer for
 * this reading on the line it sits on (TOM-860).
 */
function readingText(
  value: SignalValue,
  unit: string | null | undefined,
  category: UnitCategory | undefined,
  units: UnitSettings | undefined,
  minDecimals?: number
): string | null {
  if (typeof value === 'number')
    return formatThreshold(value, unit ?? null, category, units, { minDecimals });
  return formatValue(value, unit ?? null);
}

/**
 * The observation in words: "Now 26.5 V" only for a reading recent enough to say
 * "now" about, an age otherwise, and — for a value that came from a past
 * transition — the state change it belongs to, with its age.
 *
 * The last-transition row names the STATE CHANGE, not a firing. The boat stamps
 * that value on every transition it makes (firing, clearing, acknowledging), so
 * "when it last fired" would be a claim about which change it was, and often the
 * wrong one. Its age matters just as much: "12.1 V at its last state change"
 * with no time reads like a current reading to anyone skimming.
 *
 * A STRUCTURED path has no number to print and is nonetheless reporting —
 * `navigation.position`, which the stock `gps_loss` alarm watches. It gets its
 * own two words, on the same freshness band as every other row: "Reporting now"
 * inside it, "Last reported 3 hr ago" outside. Never "No reading", which for
 * that alarm reads as the alarm's own condition being met.
 *
 * Never blank — a row with nothing to show says so.
 *
 * `category` + `units` print the reading in the account's units (ADR 0189).
 * They convert the NUMBER ONLY — the freshness bands, the wording and the
 * timestamp are untouched, and whatever judges this observation is handed the
 * raw stored value, never this text.
 */
export function observationText(
  obs: LiveObservation,
  unit: string | null | undefined,
  now: number = Date.now(),
  category?: UnitCategory,
  units?: UnitSettings,
  minDecimals?: number
): string {
  const text = readingText(obs.value, unit, category, units, minDecimals);
  const iso = obs.atMs === null ? null : new Date(obs.atMs).toISOString();
  if (obs.source === 'lastTransition') {
    if (text === null) return 'No reading';
    const age = iso === null ? null : formatRelativeShort(iso, now);
    const at = `${text} at its last state change`;
    return age === null ? at : `${at}, ${age.toLowerCase()}`;
  }
  const relative = iso === null ? null : formatRelativeShort(iso, now);
  const fresh = iso !== null && freshness(iso, now) === 'fresh';
  if (text === null) {
    // Reported, but unprintable. Present tense ONLY inside the fresh band —
    // the same invariant the numeric arm below keeps.
    if (obs.structured) {
      if (fresh) return 'Reporting now';
      return relative === null ? 'No reading' : `Last reported ${relative.toLowerCase()}`;
    }
    return obs.source === 'none' ? 'No reading yet' : 'No reading';
  }
  if (fresh) return `Now ${text}`;
  return relative === null ? text : `${text}, ${relative.toLowerCase()}`;
}

// --- the value line (ADR 0170) ---------------------------------------------
//
// "Now 12.6 V · alarms below 11.8 V" — the owner's actual request. It is a
// PRESENTATIONAL layer beside the row view, never inside it: `toConfiguredAlarmView`
// takes no samples on purpose, so a list row renders identically offline, and
// threading live data into it would quietly break that.
//
// Both halves degrade independently and neither is ever invented. No resolved
// path (pending, unbindable, an older cloud with nothing to fall back on) ⇒ no
// line at all. A path but no reading ⇒ "No reading yet · alarms below 11.8 V".
// A reading but a trigger class this build cannot phrase ⇒ the reading alone.
//
// And both halves are in ONE vocabulary, the owner's — see
// `alarmDisplay.unitVocabulary`. The reading and the trigger arrive in STORE
// units, which the rest of the row is not written in: a recipe row's mode
// summary says "min 100 kPa" while the store domain is hPa, and every
// temperature path declares the bare glyph `C`.
// -----------------------------------------------------------------------------

/** The §5.7 `definition` fields a value line reads. Loosely typed — wire data. */
export interface ValueLineDef {
  readonly compileStatus?: string | null;
  readonly resolvedPath?: string | null;
  readonly trigger?: unknown;
  readonly custom?: unknown;
  readonly binding?: unknown;
  /**
   * The recipe id — read ONLY to tell a recipe alarm from a custom one, which
   * decides whether the row's numbers may be rescaled into the recipe's
   * authored domain. A custom alarm's set point is typed in the signal's own
   * store unit, so for it the store domain IS the owner's.
   */
  readonly recipeId?: string;
}

/** One reading restated in `vocab` — the number moved, the timestamp untouched. */
function observationIn(obs: LiveObservation, vocab: UnitVocabulary): LiveObservation {
  if (vocab.factor === 1 || typeof obs.value !== 'number') return obs;
  return { ...obs, value: obs.value * vocab.factor };
}

/**
 * ONE unit decision for a whole row: what the reading and the set point beside
 * it are both printed in.
 *
 * Two mechanisms can restate a number on these rows and they must never both
 * fire. `unitVocabulary` restates a STORE unit in the recipe's AUTHORED domain
 * (hPa → kPa) so the row agrees with the threshold table above it; ADR 0189's
 * category conversion restates it in the ACCOUNT's unit. Where a category
 * conversion actually fires, it wins and the vocabulary stands down to a pure
 * passthrough — the raw store unit goes down to the formatters, which convert
 * once, together.
 *
 * Where it does NOT fire — no category (engine oil pressure has none, by
 * design), no settings, or an account whose unit for that category already is
 * the store unit — the row falls through to exactly the code it ran before:
 * the kPa rescale for a recipe row, the `C` → `°C` glyph for everything.
 *
 * The probe value is `1` because the gate's answer does not depend on the
 * number: it compares the LABEL the category would print against the label the
 * store unit prints, and only finiteness of the value could change that.
 *
 * EXPORTED because a page that renders any OTHER part of the same row has to
 * make the identical choice, and re-deriving it is how the two halves drift
 * apart. Take `vocab` and `category` from here and pass them to every formatter
 * on the row — `observationText`, `setpointPhrase`, `clearsPhrase`,
 * `recipeTriggerExpectation` — along with the same `units`.
 *
 * A sparkline on the row follows the same fork, and it is NOT one function:
 *   - `category !== undefined` ⇒ `convertSeries(points, storeUnit, category, units)`;
 *     the account's conversion can be affine (°C → °F) and a factor cannot
 *     express it.
 *   - otherwise ⇒ `scaleSeries(points, vocab.factor)`, the recipe rescale.
 */
export function rowUnits(
  storeUnit: string | null | undefined,
  path: string,
  units: UnitSettings | undefined,
  opts: { readonly rescale?: boolean } = {}
): { vocab: UnitVocabulary; category: UnitCategory | undefined } {
  const category = categoryFor(path, storeUnit);
  if (convertedQuantity(1, storeUnit, category, units) === undefined) {
    return { vocab: unitVocabulary(storeUnit, opts), category: undefined };
  }
  const raw = (storeUnit ?? '').trim();
  return { vocab: { factor: 1, unit: raw.length > 0 ? raw : null }, category };
}

/** The live sources and the clock a value line resolves against. */
export interface ValueLineOptions {
  /** Per-path reading, from {@link observationLookup} over the page's sources. */
  readonly observationFor: (path: string) => LiveObservation;
  /** The path's declared STORE unit — the domain the wire trigger is also in. */
  readonly unitFor?: (path: string) => string | null | undefined;
  /** The account's units (`useUnits()`). Omitted ⇒ the line reads store units. */
  readonly units?: UnitSettings;
  readonly now?: number;
}

/**
 * One alarm's value line, or `null` when the alarm has no signal we may claim a
 * reading for (see {@link resolvedPathFor} — a pending definition always lands
 * here).
 *
 * The reading is worded by {@link observationText}, so a list row and the detail
 * page's live panel cannot disagree about whether a value is current: "Now
 * 12.6 V" only inside the fresh band, "12.4 V, 3 hr ago" outside it, "No reading
 * yet" for a path that has never reported.
 */
export function liveValueLine(def: ValueLineDef, options: ValueLineOptions): string | null {
  const path = resolvedPathFor(def);
  if (path === null) return null;
  // ONE vocabulary for both halves. The wire trigger is store-domain (ADR 0170)
  // and so is the reading, so labelling them differently would be the bug this
  // line exists to avoid — and printing both in a domain the REST of the row
  // isn't written in is the same bug one step out. `rowUnits` makes that one
  // decision, account units included; both halves are then handed the same
  // unit, the same category and the same settings.
  const { vocab, category } = rowUnits(options.unitFor?.(path), path, options.units, {
    rescale: !isCustomDefinition(def),
  });
  const obs = observationIn(options.observationFor(path), vocab);
  const trigger = triggerInVocabulary(readWireTrigger(def), vocab);
  // The reading, the set point and the clear point the Active screen prints
  // beside them never read as the same number when they differ (TOM-860).
  const value = typeof obs.value === 'number' ? obs.value : undefined;
  const decimalsFor = distinctDecimals(
    [value, trigger?.setpoint, trigger?.clearpoint],
    vocab.unit,
    category,
    options.units
  );
  const reading = observationText(
    obs,
    vocab.unit,
    options.now ?? Date.now(),
    category,
    options.units,
    decimalsFor(value)
  );
  const phrase = setpointPhrase(
    trigger,
    vocab.unit,
    category,
    options.units,
    decimalsFor(trigger?.setpoint)
  );
  return phrase === null ? reading : `${reading} · ${phrase}`;
}

/**
 * {@link liveValueLine} for a whole `GET /alerts/state` response, keyed by
 * definition id — one pass for both bands of the Alarms page, so the active row
 * and the configured row for the same alarm always read the same. Definitions
 * with no line are simply absent from the map.
 */
export function liveValueLines(
  rows: readonly AlarmAlertRow[],
  options: ValueLineOptions
): Map<string, string> {
  const now = options.now ?? Date.now();
  const lines = new Map<string, string>();
  for (const row of rows) {
    const line = liveValueLine(row.definition, { ...options, now });
    if (line !== null) lines.set(row.definition.id, line);
  }
  return lines;
}

// --- the panel -------------------------------------------------------------

/** One row of the panel: a signal, what it reads, and whether that satisfies it. */
export interface LiveRow {
  readonly kind: 'trigger' | 'condition';
  /** The signal's human name — "Battery 1 voltage", "the engine". */
  readonly label: string;
  /** The telemetry path behind it, when there is one (a preset has none). */
  readonly path?: string;
  /** {@link observationText} — "Now 26.5 V", "12.1 V at its last state change, 3 hr ago". */
  readonly valueText: string;
  /** What this row needs — "set point 24 V", "above 500 rpm". */
  readonly expectation?: string;
  /** true / false / cannot say, on the same three-way honesty as the builder. */
  readonly verdict: boolean | null;
  readonly chip: ClauseChip;
  readonly source: LiveValueSource;
  /**
   * False for a clause that is authored but NOT gating the alarm — the engine
   * preset the compiler could not attach (plan D2). Those rows are shown (the
   * definition carries them) but kept out of the combined verdict.
   */
  readonly inForce: boolean;
}

export interface LivePanel {
  readonly rows: LiveRow[];
  /** The combined sentence, or `null` when there is nothing honest to say. */
  readonly combined: LiveVerdictLine | null;
}

/** The stored definition fields the panel reads. */
export interface LivePanelDef {
  readonly custom?: unknown;
  readonly conditions?: unknown;
  readonly conditionUnbound?: unknown;
}

export interface LivePanelOptions {
  /** The definition as a draft (`initFromCustomDef`); `null` for a recipe alarm. */
  readonly draft: CustomAlarmDraft | null;
  /** The pinned signal's human name and declared store unit, when known. */
  readonly triggerLabel?: string;
  readonly triggerUnit?: string | null;
  /**
   * A RECIPE alarm's trigger row, from the compiled rule the cloud derived
   * (§5.7, ADR 0170) — the path it watches and the set point it watches for.
   * Used ONLY when there is no custom draft: a custom alarm states both itself,
   * and its own block is the authored truth.
   *
   * Without this a plain recipe alarm had no trigger row at all, which is why
   * "What the readings say" only ever appeared on alarms carrying a custom block
   * or conditions.
   */
  readonly recipeTrigger?: {
    readonly path: string;
    readonly trigger: AlarmWireTrigger | null;
  } | null;
  /** Human name for a clause's path — catalog-aware at the call site. */
  readonly labelFor?: (path: string) => string;
  /** A clause path's declared store unit (plan D6 — printed, never converted). */
  readonly unitFor?: (path: string) => string | null | undefined;
  /**
   * The account's units (`useUnits()`), applied to the TRIGGER row only
   * (ADR 0189). Condition clause rows stay in store units — their expectation
   * text is composed by `customAlarmConditions`, and converting one of the two
   * would put two units on one clause.
   */
  readonly units?: UnitSettings;
  readonly observationFor: (path: string) => LiveObservation;
  readonly now?: number;
}

/** The trigger row's chip. Parallel to a clause's, worded for a whole alarm. */
export function triggerChip(verdict: boolean | null): ClauseChip {
  if (verdict === true) {
    return {
      text: 'met now',
      tone: 'yes',
      detail: 'The current reading meets what sets this alarm off.',
    };
  }
  if (verdict === false) {
    return {
      text: 'not met now',
      tone: 'no',
      detail: 'The current reading does not meet what sets this alarm off.',
    };
  }
  return {
    text: 'no reading',
    tone: 'unknown',
    detail: 'Nothing recent enough to say what this alarm would do right now.',
  };
}

/** What the trigger row needs — "set point 24 V", "on", "silent for 5 min". */
export function triggerExpectation(
  draft: CustomAlarmDraft,
  unit: string | null | undefined,
  category?: UnitCategory,
  units?: UnitSettings,
  minDecimals?: number
): string | undefined {
  switch (draft.triggerClass) {
    case 'High':
    case 'Low': {
      // THROUGH THE RESOLVER, never `Number(draft.setpoint)`. Once a caller
      // hands `initFromCustomDef` an account context the box holds the OWNER's
      // number — "66" for a stored 20 m — and `formatThreshold` below converts
      // whatever it is given. Reading the box raw would therefore convert twice
      // and print a 20 m set point as 217 ft. `draftSetpointStored` answers in
      // STORE units on every draft, and on one with no units it is exactly
      // `Number(draft.setpoint)`, so this is byte-identical for those.
      const setpoint = draftSetpointStored(draft);
      if (setpoint === undefined || !Number.isFinite(setpoint)) return undefined;
      return `set point ${formatThreshold(setpoint, unit, category, units, { minDecimals })}`;
    }
    case 'On':
      return 'on';
    case 'Off':
      return 'off';
    case 'DataUnavailable': {
      const raw = (draft.staleSeconds ?? '').trim();
      const seconds = raw.length === 0 ? Number.NaN : Number(raw);
      return Number.isFinite(seconds) ? `silent for ${formatDuration(seconds * 1000)}` : undefined;
    }
    default:
      return undefined;
  }
}

/**
 * What a RECIPE alarm's trigger row needs, from the COMPILED trigger — the
 * counterpart of {@link triggerExpectation}, which reads an authored draft.
 * Same vocabulary deliberately: the two rows sit in the same column.
 *
 * `undefined` where the wire cannot answer. A `DataUnavailable` rule's window
 * rides the compiled rule's `setDelaySeconds`, which the §5.7 projection does
 * not carry, so "silent for …" has no number to state; it says nothing rather
 * than a blank quantity. An `AnchorWatch` radius lives in
 * `trigger.triggerRadius`, which the projection now LIFTS into `setpoint`
 * (`compiled-ruleset.triggerOf`) — in metres, whatever the distance path
 * declares, exactly as `setpointPhrase` treats it.
 */
export function recipeTriggerExpectation(
  trigger: AlarmWireTrigger | null | undefined,
  unit: string | null | undefined,
  category?: UnitCategory,
  units?: UnitSettings,
  minDecimals?: number
): string | undefined {
  if (trigger === null || trigger === undefined) return undefined;
  const raw = trigger.setpoint;
  const setpoint = typeof raw === 'number' && Number.isFinite(raw) ? raw : undefined;
  switch (trigger.class) {
    case 'High':
    case 'Low':
      return setpoint === undefined
        ? undefined
        : `set point ${formatThreshold(setpoint, unit, category, units, { minDecimals })}`;
    case 'On':
    case 'EntryOn':
      return 'on';
    case 'Off':
      return 'off';
    case 'AnchorWatch':
      // Metres and a short distance whatever the path declares, exactly as
      // `setpointPhrase` treats the same lifted radius.
      return setpoint === undefined
        ? undefined
        : `within ${formatThreshold(setpoint, 'm', 'shortDistance', units)}`;
    default:
      return undefined;
  }
}

/**
 * The chip on a RECIPE alarm's trigger row. The reading and the set point are
 * both on the row; the VERDICT is not ours to give.
 *
 * A custom alarm is judged (`wouldFireNow`) because the page holds its whole
 * authored spec. A recipe alarm's rule carries delays, operating modes and a
 * hysteresis band that the §5.7 trigger projection does not, so a "met now"
 * here would be a claim made from a third of the rule.
 */
const RECIPE_TRIGGER_CHIP: ClauseChip = {
  text: 'your boat decides',
  tone: 'unknown',
  detail:
    'The reading and the set point are both here. Whether the alarm fires is your boat’s call — it also applies this alarm’s delays and operating modes, which this page can’t.',
};

/** The chip for a clause the compiler could not attach (plan D2). */
const UNBOUND_CHIP: ClauseChip = {
  text: 'not attached',
  tone: 'unknown',
  detail:
    'This condition isn’t in force — your boat couldn’t attach it, so it isn’t gating the alarm.',
};

/**
 * The engine preset's own row: authored, gating on the boat, and not something
 * this page can read. The preset names no path — the compiler resolves it to the
 * matching engine's rpm on the boat — so guessing which signal to show would be
 * inventing a binding. A DROPPED preset (D2) says the more important thing
 * instead: it is not gating the alarm at all.
 */
function presetRow(clause: { readonly minRpm?: number }, inForce: boolean): LiveRow {
  const minRpm = clause.minRpm;
  return {
    kind: 'condition',
    label: 'The engine',
    valueText: inForce ? 'Checked on your boat' : 'Not being checked',
    expectation:
      minRpm !== undefined && Number.isFinite(minRpm) ? `running above ${minRpm} rpm` : 'running',
    verdict: null,
    chip: inForce
      ? {
          text: 'not shown here',
          tone: 'unknown',
          detail:
            'Your boat checks the engine itself. This page can’t show that reading, so it can’t say whether the condition is met right now.',
        }
      : UNBOUND_CHIP,
    source: 'none',
    inForce,
  };
}

/**
 * Every row of the live panel plus the combined line, for one stored definition.
 *
 * The rows follow the definition: the trigger first — from the custom block read
 * back into a draft, or failing that from the compiled rule (`recipeTrigger`,
 * ADR 0170) — then one row per stored condition clause, in stored order. Only
 * the draft row is JUDGED; see {@link RECIPE_TRIGGER_CHIP}. A clause the
 * compiler DROPPED is still shown — the definition
 * carries it — but marked out of force and kept out of the fold, exactly as
 * `alarmDisplay.alarmConditionsSetup` decides for the read-only block above it.
 *
 * The combined line is the builder's, verbatim. When it has nothing to say and
 * there IS a trigger row, the panel falls back to naming what it could not read
 * ("can’t be decided — no recent reading for Battery 1 voltage") rather than
 * leaving a silence that reads as "nothing is wrong". With no trigger row (a
 * recipe alarm, whose own trigger this page cannot evaluate) there is no
 * fallback: the per-clause chips say what is known, and a whole-alarm claim
 * would be invented.
 */
export function livePanel(def: LivePanelDef, options: LivePanelOptions): LivePanel {
  const now = options.now ?? Date.now();
  const labelFor = options.labelFor ?? ((path: string) => signalLabel(path));
  const rows: LiveRow[] = [];

  const draft = options.draft;
  let triggerVerdict: boolean | null = null;
  let triggerReadingText: string | null = null;
  let triggerLabel = options.triggerLabel ?? '';

  if (draft !== null && draft.signalPath !== undefined) {
    const obs = options.observationFor(draft.signalPath);
    // Glyph only, never a rescale: a CUSTOM alarm's set point was typed in this
    // signal's own store unit, so the store domain IS the owner's here and the
    // draft's number would move out from under the reading. The account's units
    // move BOTH (`rowUnits`), which keeps that invariant.
    const { vocab, category } = rowUnits(
      options.triggerUnit ?? options.unitFor?.(draft.signalPath),
      draft.signalPath,
      options.units
    );
    triggerLabel =
      triggerLabel.trim().length > 0 ? triggerLabel.trim() : labelFor(draft.signalPath);
    const reading = observationReading(obs);
    // Judged on the RAW stored reading against the draft's stored set point —
    // conversion is presentation and never reaches a verdict.
    triggerVerdict = wouldFireNow(draft, reading, now);
    // A reading and a set point that differ never read the same (TOM-860).
    const value = typeof obs.value === 'number' ? obs.value : undefined;
    const setpoint = draftSetpointStored(draft);
    const decimalsFor = distinctDecimals([value, setpoint], vocab.unit, category, options.units);
    // The lead quotes the reading as CURRENT, so only a source that can make a
    // present-tense claim may fill it.
    triggerReadingText =
      reading === undefined
        ? null
        : readingText(obs.value, vocab.unit, category, options.units, decimalsFor(value));
    const expectation = triggerExpectation(
      draft,
      vocab.unit,
      category,
      options.units,
      decimalsFor(setpoint)
    );
    rows.push({
      kind: 'trigger',
      label: triggerLabel,
      path: draft.signalPath,
      valueText: observationText(obs, vocab.unit, now, category, options.units, decimalsFor(value)),
      ...(expectation !== undefined ? { expectation } : {}),
      verdict: triggerVerdict,
      chip: triggerChip(triggerVerdict),
      source: obs.source,
      inForce: true,
    });
  } else if (options.recipeTrigger != null && options.recipeTrigger.path.length > 0) {
    // A RECIPE alarm: no authored draft to evaluate, but the compiled rule names
    // the path and the set point (ADR 0170), which is what the owner asked to
    // see. Shown, not judged — see RECIPE_TRIGGER_CHIP.
    const { path, trigger } = options.recipeTrigger;
    const obs = options.observationFor(path);
    // Both halves in the recipe's AUTHORED vocabulary, so this row and the
    // threshold table above it say the same quantity the same way: 1013 hPa
    // under "min 100 kPa" is two units for one number. Where the account's
    // units apply they say it instead — and the threshold table above is
    // rendered in those same units, so the two still agree.
    const { vocab, category } = rowUnits(
      options.triggerUnit ?? options.unitFor?.(path),
      path,
      options.units,
      { rescale: true }
    );
    const label = triggerLabel.trim().length > 0 ? triggerLabel.trim() : labelFor(path);
    const shownObs = observationIn(obs, vocab);
    const shownTrigger = triggerInVocabulary(trigger, vocab);
    // A reading and a set point that differ never read the same (TOM-860).
    const value = typeof shownObs.value === 'number' ? shownObs.value : undefined;
    const decimalsFor = distinctDecimals(
      [value, shownTrigger?.setpoint],
      vocab.unit,
      category,
      options.units
    );
    const expectation = recipeTriggerExpectation(
      shownTrigger,
      vocab.unit,
      category,
      options.units,
      decimalsFor(shownTrigger?.setpoint)
    );
    rows.push({
      kind: 'trigger',
      label,
      path,
      valueText: observationText(
        shownObs,
        vocab.unit,
        now,
        category,
        options.units,
        decimalsFor(value)
      ),
      ...(expectation !== undefined ? { expectation } : {}),
      verdict: null,
      chip: RECIPE_TRIGGER_CHIP,
      source: obs.source,
      inForce: true,
    });
  }

  const stored = readConditions(def.conditions);
  const clauses: readonly ConditionClauseSpec[] = stored?.clauses ?? [];
  // `alarmConditionsSetup` owns the "is this clause in force" decision (a dropped
  // preset under `all`, the WHOLE block under `any`). Its rows are 1:1 with the
  // readable clauses, so they zip by index; a length mismatch from a future
  // change degrades to "in force", which is what the definition literally says.
  const setupRows = alarmConditionsSetup(def, {
    ...(options.unitFor !== undefined ? { unitFor: options.unitFor } : {}),
  })?.rows;
  const clauseStates: ClauseState[] = [];

  clauses.forEach((clause, index) => {
    const inForce = setupRows?.[index]?.unbound !== true;
    if ('preset' in clause) {
      const row = presetRow(clause, inForce);
      rows.push(row);
      if (inForce) {
        clauseStates.push({
          label: 'the engine',
          verdict: null,
          ...(row.expectation !== undefined ? { expectation: row.expectation } : {}),
        });
      }
      return;
    }
    const path = clause.path;
    const editable: ConditionDraft | undefined = conditionDraftFromClause(clause);
    // Glyph only. A clause threshold is typed in the signal's own store unit, so
    // the number must not move — but it should be SPELLED the way the read-only
    // conditions block above this panel spells it.
    const unit = unitVocabulary(options.unitFor?.(path)).unit;
    const obs = options.observationFor(path);
    const reading = observationReading(obs);
    const verdict = editable === undefined ? null : clauseHolds(editable, reading, now);
    const label = labelFor(path);
    const expectation = editable === undefined ? undefined : clauseExpectation(editable, unit);
    const readingText = formatValue(obs.value, unit);
    rows.push({
      kind: 'condition',
      label,
      path,
      valueText: observationText(obs, unit, now),
      ...(expectation !== undefined ? { expectation } : {}),
      verdict: inForce ? verdict : null,
      chip: inForce ? clauseChip(verdict) : UNBOUND_CHIP,
      source: obs.source,
      inForce,
    });
    if (inForce) {
      clauseStates.push({
        label,
        verdict,
        ...(expectation !== undefined ? { expectation } : {}),
        ...(reading === undefined ? {} : { readingText }),
      });
    }
  });

  const logic = stored?.logic === 'any' ? 'any' : 'all';
  const line = liveVerdictLine({
    trigger: triggerVerdict,
    ...(triggerReadingText === null ? {} : { triggerReadingText }),
    clauses: clauseStates,
    logic,
  });

  if (line !== null) return { rows, combined: line };
  if (rows.length === 0 || draft === null) return { rows, combined: null };
  // The trigger is the unknown (a false clause would already have produced a
  // line): say which signal we are missing rather than going quiet.
  const named = triggerLabel.trim().length > 0 ? triggerLabel.trim() : 'this alarm’s signal';
  return {
    rows,
    combined: {
      lead: 'With the current readings, this alarm',
      verdict: 'can’t be decided',
      tone: 'unknown',
      reason: `no recent reading for ${named}`,
    },
  };
}
